import React, { useMemo } from 'react';
import { DayOfWeekBreakdown, HourOfDayBreakdown, identifyPeakHours } from '../../utils/breakdownUtils';
import './ByTimeTab.css';

interface ByTimeTabProps {
  dayOfWeekBreakdowns: DayOfWeekBreakdown[];
  hourOfDayBreakdowns: HourOfDayBreakdown[];
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const ByTimeTab: React.FC<ByTimeTabProps> = ({
  dayOfWeekBreakdowns,
  hourOfDayBreakdowns,
  isLoading,
  error,
  onRetry,
}) => {
  // Identify strongest and weakest days
  const { strongestDay, weakestDay } = useMemo(() => {
    if (dayOfWeekBreakdowns.length === 0) return { strongestDay: null, weakestDay: null };
    
    const sorted = [...dayOfWeekBreakdowns].sort((a, b) => b.net_profit - a.net_profit);
    return {
      strongestDay: sorted[0],
      weakestDay: sorted[sorted.length - 1],
    };
  }, [dayOfWeekBreakdowns]);

  // Identify peak hours
  const peakHours = useMemo(() => {
    return identifyPeakHours(hourOfDayBreakdowns, 3);
  }, [hourOfDayBreakdowns]);

  const formatCurrency = (value: number) => {
    const sign = value < 0 ? '-' : '';
    const absValue = Math.abs(value);
    return `${sign}$${absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getProfitClass = (profit: number) => {
    if (profit > 0) return 'profit-positive';
    if (profit < 0) return 'profit-negative';
    return 'profit-neutral';
  };

  const formatHour = (hour: number) => {
    if (hour === 0) return '12am';
    if (hour < 12) return `${hour}am`;
    if (hour === 12) return '12pm';
    return `${hour - 12}pm`;
  };

  const formatHourRange = (hours: number[]) => {
    if (hours.length === 0) return 'N/A';
    if (hours.length === 1) return formatHour(hours[0]);
    return `${formatHour(hours[0])}-${formatHour(hours[hours.length - 1])}`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="breakdown-loading">
        <div className="breakdown-loading-spinner" />
        <span>Calculating time patterns...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="breakdown-error">
        <div className="breakdown-error-title">Failed to load time patterns</div>
        <div className="breakdown-error-message">{error}</div>
        {onRetry && (
          <button className="breakdown-retry-button" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  // Empty state
  if (dayOfWeekBreakdowns.length === 0 && hourOfDayBreakdowns.length === 0) {
    return (
      <div className="breakdown-empty">
        <div className="breakdown-empty-icon">📅</div>
        <div className="breakdown-empty-title">No time pattern data</div>
        <div className="breakdown-empty-message">
          Insufficient data to analyze time patterns.
        </div>
        <div className="breakdown-empty-suggestion">
          Select a date range spanning multiple days to see patterns.
        </div>
      </div>
    );
  }

  return (
    <div className="by-time-tab">
      {/* Day of Week Analysis */}
      {dayOfWeekBreakdowns.length > 0 && (
        <div className="time-section">
          <h4 className="time-section-title">Day of Week Analysis</h4>
          
          {/* Insights */}
          {strongestDay && weakestDay && (
            <div className="time-insights">
              <div className="insight-item">
                💡 <strong>{strongestDay.day_name}</strong> is your strongest day 
                ({formatPercent(strongestDay.percent_of_total)} of profit, {formatPercent(strongestDay.win_rate)} win rate)
              </div>
            </div>
          )}

          <div className="day-breakdown-container">
            {dayOfWeekBreakdowns.map((day) => (
              <div key={day.day_of_week} className="day-row">
                <div className="day-info">
                  <div className="day-name">{day.day_name}</div>
                  <div className="day-stats">
                    <span className={`day-profit ${getProfitClass(day.net_profit)}`}>
                      {formatCurrency(day.net_profit)}
                    </span>
                    <span className="day-separator">•</span>
                    <span className="day-win-rate">
                      {formatPercent(day.win_rate)} win rate
                    </span>
                  </div>
                </div>
                <div className="day-visual">
                  <div className="day-bar-container">
                    <div 
                      className={`day-bar ${getProfitClass(day.net_profit)}`}
                      style={{ width: `${Math.min(Math.abs(day.percent_of_total), 100)}%` }}
                    />
                  </div>
                  <div className="day-percent">{formatPercent(day.percent_of_total)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hour of Day Analysis */}
      {hourOfDayBreakdowns.length > 0 && (
        <div className="time-section">
          <h4 className="time-section-title">Hour of Day Analysis</h4>
          
          {/* Insights */}
          {peakHours.length > 0 && (
            <div className="time-insights">
              <div className="insight-item">
                💡 Peak profitability: {formatHourRange(peakHours)}
              </div>
            </div>
          )}

          <div className="hour-chart-container">
            <div className="hour-chart">
              {hourOfDayBreakdowns.map((hour) => {
                const maxProfit = Math.max(...hourOfDayBreakdowns.map(h => Math.abs(h.net_profit)));
                const barHeight = maxProfit > 0 ? (Math.abs(hour.net_profit) / maxProfit) * 100 : 0;
                const isPeak = peakHours.includes(hour.hour);

                return (
                  <div key={hour.hour} className="hour-column">
                    <div className="hour-bar-wrapper">
                      <div 
                        className={`hour-bar ${getProfitClass(hour.net_profit)} ${isPeak ? 'peak' : ''}`}
                        style={{ height: `${barHeight}%` }}
                        title={`${formatHour(hour.hour)}: ${formatCurrency(hour.net_profit)}`}
                      >
                        {hour.net_profit !== 0 && (
                          <span className="hour-value">{formatCurrency(hour.net_profit)}</span>
                        )}
                      </div>
                    </div>
                    <div className="hour-label">{formatHour(hour.hour)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hour-legend">
            <div className="legend-item">
              <div className="legend-color profit-positive"></div>
              <span>Positive</span>
            </div>
            <div className="legend-item">
              <div className="legend-color profit-negative"></div>
              <span>Negative</span>
            </div>
            {peakHours.length > 0 && (
              <div className="legend-item">
                <div className="legend-color peak"></div>
                <span>Peak Hour</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ByTimeTab;
